import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import CourseCard from '../components/CourseCard';
import { getCachedCourses, setCachedCourses } from '../utils/courseCache';
import Seo from '../components/Seo';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';

/* ─── ICONS ───────────────────────────────────────────────── */
const BriefcaseIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

/* ─── CIRCUIT BG ──────────────────────────────────────────── */
const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
        <circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const DURATION_OPTIONS = [
  { label: 'All Durations', value: '' },
  { label: '15 Days', value: '15days' },
  { label: '1 Month', value: '1month' },
  { label: '2 Months', value: '2month' },
  { label: '3 Months', value: '3month' },
];

const PERKS = [
  'Industry project experience',
  'Verifiable internship certificate',
  'Mentorship from working professionals',
  'LinkedIn-ready portfolio projects',
  'Letter of Recommendation (on merit)',
  'Placement assistance after completion',
];

const Internships = () => {
  const [internships, setInternships] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [durationFilter, setDurFilter] = useState('');
  const [level, setLevel] = useState('All');

  useEffect(() => {
    setError(null);

    // Check client cache first
    const cacheKey = `internships_${durationFilter}`;
    const cached = getCachedCourses(cacheKey);
    if (cached) {
      setInternships(cached);
      setFiltered(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const params = new URLSearchParams({ type: 'internship' });
    if (durationFilter) params.append('durationFilter', durationFilter);
    API.get(`/courses?${params}`, { signal: controller.signal })
      .then(res => {
        setInternships(res.data);
        setFiltered(res.data);
        setCachedCourses(cacheKey, res.data);
      })
      .catch(err => { if (err.name !== 'CanceledError') setError('Failed to load internships. Please try again.'); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [durationFilter]);

  useEffect(() => {
    let result = internships;
    if (search) result = result.filter(c =>
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      c.category.toLowerCase().includes(search.toLowerCase())
    );
    if (level !== 'All') result = result.filter(c => c.level === level);
    setFiltered(result);
  }, [search, level, internships]);

  const btnBase = {
    padding: '9px 18px', borderRadius: 8, fontFamily: "'Satoshi','DM Sans',sans-serif",
    fontWeight: 700, fontSize: 13.5, cursor: 'pointer', border: '1.5px solid', transition: 'all 0.2s',
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Seo
        title="Internships"
        description="Real internships with ThiranX — gain hands-on industry experience, work on live projects, and earn a verified certificate to boost your career."
        path="/internships"
      />

      {/* ══ HERO ═══════════════════════════════════════════════ */}
      <section style={{ minHeight: '64vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', padding: '120px 0 80px', background: 'var(--bg)' }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="intern-hero-grid">

            {/* Left */}
            <div>
              <Link to="/courses" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 28,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = V2}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
              >
                <ArrowLeftIcon /> Back to Courses
              </Link>

              <div className="section-label">INTERNSHIP PROGRAMME</div>
              <h1 style={{
                fontSize: 'clamp(36px,6vw,68px)', fontWeight: 800, lineHeight: 1.1,
                letterSpacing: '-0.03em', marginBottom: 20,
                fontFamily: "'Inter',sans-serif",
              }}>
                Real Experience.<br />
                <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Real Certificate.</span>
              </h1>
              <p style={{ color: 'var(--text-2)', fontSize: 'clamp(15px,1.8vw,18px)', maxWidth: 480, lineHeight: 1.8, marginBottom: 36 }}>
                ThiranX internships are structured programmes where you work on real projects with industry mentors — and walk away with a verifiable certificate.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="#internship-list" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: `linear-gradient(135deg,${V1},${V3})`,
                  color: '#fff', padding: '14px 32px', borderRadius: 12, fontWeight: 800, fontSize: 15,
                  fontFamily: "'Satoshi','DM Sans',sans-serif",
                  boxShadow: `0 8px 28px ${V1}44`, transition: 'all 0.22s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.opacity = '1'; }}
                >
                  <BriefcaseIcon size={17} /> Browse Internships
                </a>
              </div>
            </div>

            {/* Right: perks */}
            <div style={{
              background: 'var(--card-solid)', border: `1px solid ${V1}33`,
              borderRadius: 24, padding: '32px 28px',
              boxShadow: `0 16px 64px ${V1}18`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg,${V4},${V3})`, border: `1px solid ${V1}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: V2 }}>
                  <BriefcaseIcon />
                </div>
                <div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 17 }}>What You Get</div>
                  <div style={{ color: 'var(--muted)', fontSize: 13 }}>Every internship includes</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {PERKS.map((perk, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(13,186,121,0.12)', border: '1px solid rgba(13,186,121,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--success)', flexShrink: 0 }}>
                      <CheckIcon />
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>{perk}</span>
                  </div>
                ))}
              </div>
              {/* Duration chips */}
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                  Available Durations
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['15 Days', '1 Month', '2 Months', '3 Months'].map(d => (
                    <div key={d} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'var(--badge-pill-bg)', border: '1px solid var(--badge-pill-border)',
                      borderRadius: 999, padding: '5px 14px',
                      fontSize: 12.5, fontWeight: 700, color: 'var(--badge-pill-text)',
                    }}>
                      <ClockIcon /> {d}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FILTERS & LIST ═════════════════════════════════════ */}
      <section id="internship-list" style={{ padding: '60px 0 120px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 40 }}>
            <div className="section-label">AVAILABLE INTERNSHIPS</div>
            <h2 style={{ fontSize: 'clamp(26px,3.5vw,44px)', fontWeight: 800, letterSpacing: '-0.025em' }}>
              Find Your <span style={{ background: `linear-gradient(135deg,${V1},${V2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Internship</span>
            </h2>
          </div>

          {/* Row 1: Search + Level */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text" placeholder="Search internships..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, minWidth: 200, background: 'var(--card-solid)',
                border: `1.5px solid var(--border)`, borderRadius: 10,
                color: 'var(--text)', padding: '12px 16px', fontSize: 14.5, outline: 'none',
                fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.2s',
              }}
              onFocus={e => { e.target.style.borderColor = `${V1}77`; e.target.style.boxShadow = `0 0 0 3px ${V1}18`; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['All', 'Beginner', 'Intermediate', 'Advanced'].map(l => (
                <button key={l} onClick={() => setLevel(l)} style={{
                  ...btnBase,
                  background: level === l ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                  color: level === l ? '#fff' : 'var(--muted)',
                  borderColor: level === l ? V1 : 'var(--border)',
                  boxShadow: level === l ? `0 4px 16px ${V1}33` : 'none',
                }}>{l}</button>
              ))}
            </div>
          </div>

          {/* Row 2: Duration Filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 48, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: 'var(--muted)', fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 4 }}>Duration:</span>
            {DURATION_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setDurFilter(opt.value)} style={{
                ...btnBase, padding: '8px 16px',
                background: durationFilter === opt.value ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                color: durationFilter === opt.value ? '#fff' : 'var(--muted)',
                borderColor: durationFilter === opt.value ? V1 : 'var(--border)',
                boxShadow: durationFilter === opt.value ? `0 4px 16px ${V1}33` : 'none',
              }}>{opt.label}</button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:28 }}>
                <div style={{ width:20, height:20, borderRadius:'50%', border:`2.5px solid ${V1}33`, borderTopColor:V1, animation:'spin 0.8s linear infinite' }} />
                <span style={{ color:'var(--muted)', fontSize:14, fontWeight:600 }}>
                  Loading internships
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                  <span className="loading-dot" style={{ display:'inline-block' }}>.</span>
                </span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:28 }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton-card">
                    <div className="skeleton-thumb" />
                    <div className="skeleton-body">
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div className="skeleton-bone" style={{ width:80, height:22 }} />
                        <div className="skeleton-bone" style={{ width:70, height:22 }} />
                      </div>
                      <div className="skeleton-bone" style={{ width:'85%', height:18 }} />
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        <div className="skeleton-bone" style={{ width:'100%', height:13 }} />
                        <div className="skeleton-bone" style={{ width:'72%', height:13 }} />
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:'1px solid var(--border)' }}>
                        <div className="skeleton-bone" style={{ width:80, height:14 }} />
                        <div className="skeleton-bone" style={{ width:90, height:14 }} />
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div className="skeleton-bone" style={{ width:70, height:28 }} />
                        <div className="skeleton-bone" style={{ width:100, height:36, borderRadius:10 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
              <div style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 20 }}>{error}</div>
              <button onClick={() => { setCachedCourses(`internships_${durationFilter}`, null); setDurFilter(f => f); }} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg,${V1},${V3})`, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif" }}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '80px 24px',
              background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20,
            }}>
              <div style={{ color: V2, display: 'flex', justifyContent: 'center', marginBottom: 14 }}><BriefcaseIcon size={40} /></div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 20, marginBottom: 10 }}>
                No internships found
              </div>
              <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>
                Try a different duration or check back soon — new programmes are added regularly.
              </p>
              <button onClick={() => { setDurFilter(''); setLevel('All'); setSearch(''); }} style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg,${V1},${V3})`,
                color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                fontFamily: "'Satoshi','DM Sans',sans-serif",
              }}>
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 28, marginBottom: 48 }}>
                {filtered.map(c => <CourseCard key={c._id} course={c} />)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 20, padding: '28px 32px', borderRadius: 18, background: 'var(--card-solid)', border: `1px solid var(--border)` }}>
                {[
                  { val: filtered.length, label: 'Internships Shown' },
                  { val: internships.length, label: 'Total Programmes' },
                  { val: '500+', label: 'Alumni Placed' },
                ].map(({ val, label }) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 900, fontSize: 30, color: V2, marginBottom: 4 }}>{val}</div>
                    <div style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 600 }}>{label}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 860px) { .intern-hero-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

export default Internships;
